import React, { useEffect, useState, useCallback } from "react";
import { Comment } from "../types/auth";
import { getTaskComments, createComment } from "../services/authService";
import "./CommentsModal.css";

interface CommentsModalProps {
  isOpen: boolean;
  taskId: string;
  taskName: string;
  canCreateComment: boolean;
  canViewComments: boolean;
  onClose: () => void;
  user: { id: string; displayName: string } | null;
}

export const CommentsModal: React.FC<CommentsModalProps> = ({
  isOpen,
  taskId,
  taskName,
  canCreateComment,
  canViewComments,
  onClose,
  user: _user,
}) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadComments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (canViewComments) {
        const data = await getTaskComments(taskId);
        setComments(data);
      } else {
        setComments([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load comments");
    } finally {
      setLoading(false);
    }
  }, [taskId, canViewComments]);

  useEffect(() => {
    if (!isOpen) return;
    loadComments();
  }, [isOpen, loadComments]);

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !canCreateComment) return;

    setSubmitting(true);
    setError(null);
    try {
      const comment = await createComment(taskId, newComment.trim());
      setComments([comment, ...comments]);
      setNewComment("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to post comment");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="comments-modal-overlay" onClick={onClose}>
      <div
        className="modal-content comments-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>Comments for: {taskName}</h2>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-body comments-modal-body">
          {!canViewComments ? (
            <div className="permission-message">
              You don't have permission to view comments.
            </div>
          ) : loading ? (
            <div className="loading">Loading comments...</div>
          ) : comments.length === 0 ? (
            <div className="empty-message">No comments yet</div>
          ) : (
            <div className="comments-list">
              {comments.map((comment) => (
                <div key={comment.id} className="comment-item">
                  <div className="comment-header">
                    <strong>{comment.userDisplayName}</strong>
                    <span className="comment-date">
                      {new Date(comment.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <div className="comment-content">{comment.content}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {canCreateComment && (
          <div className="modal-footer comments-modal-footer">
            <form onSubmit={handleSubmitComment} className="comment-form">
              {error && <div className="error-message">{error}</div>}
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                rows={3}
                className="comment-input"
                disabled={submitting}
              />
              <button
                type="submit"
                className="btn-primary"
                disabled={submitting || !newComment.trim()}
              >
                {submitting ? "Posting..." : "Post Comment"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};
